import { HttpClient, HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { AuthService } from '../auth/auth.service';
import { CmpsDatePipe } from '../common/date.pipe';
import {
    IFrameDirective,
    IFrameService,
    IFrameSubscriber,
} from '../common/iframe.directive';
import { ModalWebFormComponent } from '../webform/modal-webform.component';
import { WidgetConfiguration } from '../widget/config.widget.model';
import { PeriodType } from '../widget/date-range.widget.model';
import { ModalWidgetComponent } from '../widget/modal.widget.component';
import { CMPS_WIDGET_CONFIG } from '../widget/widget.metadata';
import { WidgetService } from '../widget/widget.service';
import { createWebFormsScorecardUrl } from '../widget/widget.util';

import { TranslationKeyMap } from './legacy-widget.i18n';
import { ScorecardListWidgetComponent } from './scorecard-list.widget.component';
import { ScorecardListPopupItem } from './scorecard-list.widget.model';

@Component({
    selector: 'cmps-widget-external-content',
    templateUrl: './external-content.widget.component.html',
})
export class ExternalContentWidgetComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('iframe', { static: true })
    iframe: IFrameDirective;

    ready: boolean;
    protected _iframeSub: Subscription;
    private _encryptionType: number;
    private _params: any;
    private _rawUrl: string;
    protected _pgsSub: Subscription;
    url: string;
    currentStartDate: Date;
    currentPeriodType: PeriodType;
    private _modal: NgbModalRef;

    get isPgs(): boolean {
        return this._encryptionType > 0;
    }

    constructor(
        private readonly _http: HttpClient,
        private readonly _modalService: NgbModal,
        private readonly _translateService: TranslateService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        @Inject(CMPS_WIDGET_CONFIG)
        private readonly _config: WidgetConfiguration,
        protected _iFrameService: IFrameService,
        private readonly _authService: AuthService,
        private readonly _widgetService: WidgetService,
        private readonly _datePipe: CmpsDatePipe,
    ) {
        this._evalAttributes();
    }

    ngOnInit(): void {
        if (this._rawUrl == null) {
            this.ready = true;
            return;
        }
        let tempUrl = this._rawUrl;
        for (const i in this._params) {
            let paramNum = null;
            let param = null;
            try {
                paramNum = i.replace('param', '');
            } catch (err) {}
            if (this._params[i] === 'userId') {
                param = this._authService.user.id;
            }
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            tempUrl = tempUrl.replace(`{${paramNum}}`, param);
        }
        if (this._encryptionType !== 0) {
            const params = new HttpParams()
                .set('userID', this._authService.user.id.toString())
                .set('widgetID', '8');
            this._http
                .get(
                    `${this._appConfig.API_ENDPOINTS.WEB_FORMS}/auth/GetPgsURL`,
                    { params },
                )
                .subscribe(
                    (r: { URL: string }) => {
                        this.url = r.URL;
                    },
                    // eslint-disable-next-line n/handle-callback-err
                    (error) => null,
                    () => null,
                );
        } else {
            this.url = tempUrl;
        }
    }

    ngAfterViewInit(): void {
        this._iframeSub = this._iFrameService.messages.subscribe(
            new IFrameSubscriber<any>(this.iframe.id, (message) => {
                // console.log(message, this.iframe.id);
                if (!this.isPgs && message.event === 'DOMContentLoaded') {
                    this._iFrameService.postMessage(this.iframe, {
                        method: 'getClientHeight',
                    });
                }
                if (message.responseTo === 'getClientHeight') {
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/restrict-plus-operands
                    this.iframe.el.nativeElement.style.height = `${
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/restrict-plus-operands
                        message.clientHeight + 20
                    }px`;
                }
                if (message.event === 'widgetPopup') {
                    this._showModal(message);
                }
            }),
        );
    }

    ngOnDestroy(): void {
        if (this._modal) {
            this._modal.close();
        }
        if (this._pgsSub) {
            this._pgsSub.unsubscribe();
        }
        if (this._iframeSub) {
            this._iframeSub.unsubscribe();
        }
    }

    // TODO revise
    private _evalAttributes(): void {
        this._params = {};
        this._config.attributes.forEach((attr) => {
            // if (attr.name === '#ofParameters')
            if (attr.name === 'EncryptionType')
                this._encryptionType =
                    attr.value == null || attr.value.length === 0
                        ? 0
                        : parseInt(attr.value);
            if (attr.name.startsWith('param'))
                this._params[attr.name] = attr.value;
            if (attr.name === 'URL') this._rawUrl = attr.value;
        });
    }

    private _showModal(message: any): void {
        this.currentStartDate = null;
        this.currentPeriodType = !this._widgetService.dateRange.value
            ? PeriodType.MONTH
            : this._widgetService.dateRange.value.type;
        if (message.url.indexOf('TMAgentListScorecardpopup') !== -1) {
            if (message.params) {
                for (const i in message.params) {
                    const p = message.params[i];
                    if (i.toLowerCase() === 'datestart' && p != null) {
                        this.currentStartDate = p;
                    }
                    if (
                        i.toLowerCase() === 'periodtypeid' &&
                        p != null &&
                        p.length !== 0
                    ) {
                        this.currentPeriodType = parseInt(p);
                    }
                }
            }
            // TODO dedup. see: team-scorecard-chart.widget
            this._modal = this._modalService.open(ModalWidgetComponent, {
                size: 'lg',
            });
            this._modal.componentInstance.config = {
                header: this._config.title,
                component: ScorecardListWidgetComponent,
                options: {
                    startDate: this.currentStartDate,
                },
            };
            this._modal.result.then(
                (result) => {
                    this._modal = null;
                    if (typeof result === 'object') {
                        this._showScorecard(result.item);
                    }
                    // eslint-disable-next-line no-return-assign
                },
                (reason) => (this._modal = null),
            );
        } else {
            const header = this._translateService.instant(
                TranslationKeyMap._802_SCORECARD___SUBTITLE_,
                { subtitle: this._authService.user.securityInfo.username },
            );
            let params = new HttpParams();
            if (message.params) {
                for (const i in message.params) {
                    const p = message.params[i];
                    params = params.append(i, p == null ? '' : p.toString());
                }
            }
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            this._showScorecardModal(
                header,
                `${this._appConfig.WEB_FORMS_BASE_URL}${
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    message.url
                }?${params.toString()}`,
            );
        }
    }

    // TODO dedup. see: team-scorecard-chart.widget
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _showScorecard(item: ScorecardListPopupItem) {
        const header = this._translateService.instant(
            TranslationKeyMap._802_SCORECARD___SUBTITLE_,
            { subtitle: item.username },
        );
        const url = `${
            this._appConfig.WEB_FORMS_BASE_URL
        }${createWebFormsScorecardUrl(this._datePipe, {
            id: item.scorecardId,
            name: item.scorecardName,
            userId: item.userId,
            username: item.username,
            startDate: this.currentStartDate || item.startDate,
            endDate: null,
            periodTypeId: this.currentPeriodType,
        })}`;
        this._showScorecardModal(header, url);
    }

    private _showScorecardModal(header: string, url: string): void {
        this._modal = this._modalService.open(ModalWebFormComponent, {
            windowClass: 'cmps-iframe-scorecard',
        });
        this._modal.componentInstance.config = {
            header,
            currentUrl: url,
        };
        this._modal.componentInstance.hasScrolling = false;
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }
}
